import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
      this.#scrollToNewest();
    }
  };

  /**
   * CREU (revisión 29.07 de Alba): "los más recientes se añaden abajo y la vista
   * se posiciona abajo, con scroll hacia arriba para ver los anteriores".
   *
   * Shopify ya devuelve las líneas por orden de adición, así que lo nuevo queda
   * al final; lo que falta es dejar la vista ahí. El contenido del cajón se
   * repinta DESPUÉS del evento de añadir, por eso no vale con hacerlo una vez:
   * se reintenta unas cuantas veces hasta que el alto deja de cambiar.
   */
  #scrollToNewest() {
    let intentos = 0;
    let altoPrevio = -1;

    const bajar = () => {
      const dialog = this.refs?.dialog ?? this.querySelector('dialog');
      if (!dialog) return;

      // Quién scrollea de verdad depende del tema: aquí es un <scroll-hint
      // class="cart-drawer__items">, no el diálogo ni un div con nombre
      // previsible. Por eso se busca por comportamiento (overflow + contenido
      // que no cabe) en vez de por una lista de selectores.
      const scrollable = [...dialog.querySelectorAll('*'), dialog].find((el) => {
        if (el.scrollHeight <= el.clientHeight + 4) return false;
        const ov = getComputedStyle(el).overflowY;
        return ov === 'auto' || ov === 'scroll';
      });
      if (scrollable) {
        scrollable.scrollTop = scrollable.scrollHeight;
        if (scrollable.scrollHeight === altoPrevio) return; // ya está estable
        altoPrevio = scrollable.scrollHeight;
      }

      if (++intentos < 8) setTimeout(bajar, 120);
    };

    requestAnimationFrame(bajar);
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
